import React, { useState, useEffect, useCallback } from 'react';
import { adminPurchasesAPI } from '../../services/api';

const YELLOW = '#F5E642';

const SectionHeader = ({ children }) => (
  <h3 className="text-brand-yellow font-bold uppercase tracking-wider text-sm mb-4">
    {children}
  </h3>
);

const SortIcon = ({ active, dir }) => {
  if (!active) return <span className="ml-1 opacity-30">&#8597;</span>;
  return (
    <span className="ml-1" style={{ color: YELLOW }}>
      {dir === 'asc' ? '↑' : '↓'}
    </span>
  );
};

const PurchasesDetail = ({ onBack }) => {
  const [purchases, setPurchases] = useState([]);
  const [meta, setMeta] = useState({ total_count: 0, page: 1, per_page: 25, total_pages: 1 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [sort, setSort] = useState('purchased_at');
  const [dir, setDir] = useState('desc');
  const [page, setPage] = useState(1);

  // Debounce the search input by 350ms to avoid hammering the API on every keystroke
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
      setPage(1);
    }, 350);
    return () => clearTimeout(timer);
  }, [search]);

  const fetchPurchases = useCallback(() => {
    setLoading(true);
    setError(null);

    const params = { sort, dir, page, per_page: 25 };
    if (debouncedSearch) params.q = debouncedSearch;

    adminPurchasesAPI
      .list(params)
      .then((res) => {
        setPurchases(res.data.purchases || []);
        setMeta(res.data.meta || {});
      })
      .catch(() => setError('Failed to load purchases.'))
      .finally(() => setLoading(false));
  }, [sort, dir, page, debouncedSearch]);

  useEffect(() => {
    fetchPurchases();
  }, [fetchPurchases]);

  const handleSort = (col) => {
    if (sort === col) {
      setDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSort(col);
      setDir('desc');
    }
    setPage(1);
  };

  const formatDate = (iso) => {
    if (!iso) return '';
    const d = new Date(iso);
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  return (
    <div className="space-y-4" data-testid="purchases-detail">
      {/* Header row */}
      <div className="flex items-center gap-4">
        <button
          onClick={onBack}
          className="text-gray-400 hover:text-brand-yellow text-sm font-semibold uppercase tracking-wider transition-colors"
          data-testid="purchases-back-btn"
        >
          &larr; Back
        </button>
        <SectionHeader>Purchases</SectionHeader>
        {meta.total_count > 0 && (
          <span className="text-gray-500 text-sm ml-auto">
            {meta.total_count.toLocaleString()} total
          </span>
        )}
      </div>

      {/* Search box */}
      <div>
        <input
          type="text"
          placeholder="Search by email, username, or battle title..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="w-full bg-battle-gray border border-army/30 rounded-lg px-4 py-2 text-white text-sm placeholder-gray-500 focus:outline-none focus:border-brand-yellow transition-colors"
          data-testid="purchases-search"
        />
      </div>

      {/* Table */}
      {loading ? (
        <div className="flex justify-center py-16" data-testid="purchases-loading">
          <div className="w-7 h-7 border-2 border-army border-t-brand-yellow rounded-full animate-spin" />
        </div>
      ) : error ? (
        <div className="py-12 text-center text-red-400 text-sm" data-testid="purchases-error">
          {error}
        </div>
      ) : purchases.length === 0 ? (
        <div className="py-12 text-center text-gray-500 text-sm" data-testid="purchases-empty">
          No purchases found.
        </div>
      ) : (
        <div className="overflow-x-auto rounded-lg border border-army/20" data-testid="purchases-table">
          <table className="w-full text-sm">
            <thead className="bg-battle-gray">
              <tr className="text-gray-400 text-xs uppercase tracking-wider">
                <th
                  className="text-left py-3 px-4 font-semibold cursor-pointer select-none hover:text-brand-yellow transition-colors whitespace-nowrap"
                  onClick={() => handleSort('purchased_at')}
                  data-testid="sort-purchased-at"
                >
                  Date
                  <SortIcon active={sort === 'purchased_at'} dir={dir} />
                </th>
                <th className="text-left py-3 px-4 font-semibold">Customer</th>
                <th className="text-left py-3 px-4 font-semibold">Battle</th>
                <th
                  className="text-right py-3 px-4 font-semibold cursor-pointer select-none hover:text-brand-yellow transition-colors whitespace-nowrap"
                  onClick={() => handleSort('amount_cents')}
                  data-testid="sort-amount"
                >
                  Amount
                  <SortIcon active={sort === 'amount_cents'} dir={dir} />
                </th>
                <th className="text-left py-3 px-4 font-semibold whitespace-nowrap">Provider</th>
                <th className="text-left py-3 px-4 font-semibold whitespace-nowrap">Payment ID</th>
              </tr>
            </thead>
            <tbody>
              {purchases.map((p, i) => (
                <tr
                  key={p.id}
                  className={`border-t border-army/10 ${i % 2 === 1 ? 'bg-black/10' : ''} hover:bg-army/10 transition-colors`}
                >
                  <td className="py-3 px-4 text-gray-300 whitespace-nowrap">
                    {formatDate(p.purchased_at)}
                  </td>
                  <td className="py-3 px-4">
                    <div className="text-white font-medium truncate max-w-[180px]">
                      {p.user?.display_name || p.user?.username || ''}
                    </div>
                    <div className="text-gray-400 text-xs truncate max-w-[180px]">
                      {p.user?.email || ''}
                    </div>
                  </td>
                  <td className="py-3 px-4 text-gray-300 truncate max-w-[200px]">
                    {p.video?.title || ''}
                  </td>
                  <td className="py-3 px-4 text-right text-brand-yellow font-bold whitespace-nowrap">
                    {p.amount}
                  </td>
                  <td className="py-3 px-4 text-gray-400 whitespace-nowrap capitalize">
                    {p.payment_provider || ''}
                  </td>
                  <td className="py-3 px-4 text-gray-500 font-mono text-xs whitespace-nowrap">
                    {p.payment_id || ''}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Pagination controls */}
      {!loading && !error && meta.total_pages > 1 && (
        <div className="flex items-center justify-between pt-2" data-testid="purchases-pagination">
          <button
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page <= 1}
            className="px-4 py-2 rounded-lg text-sm font-semibold border border-army/30 text-gray-300 hover:border-brand-yellow hover:text-brand-yellow disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
            data-testid="pagination-prev"
          >
            Prev
          </button>
          <span className="text-gray-400 text-sm" data-testid="pagination-info">
            Page {page} of {meta.total_pages}
          </span>
          <button
            onClick={() => setPage((p) => Math.min(meta.total_pages, p + 1))}
            disabled={page >= meta.total_pages}
            className="px-4 py-2 rounded-lg text-sm font-semibold border border-army/30 text-gray-300 hover:border-brand-yellow hover:text-brand-yellow disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
            data-testid="pagination-next"
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
};

export default PurchasesDetail;
